/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PRODUCT_TYPE } from 'configs/constants';
import monitoringService from 'infrastructure/serviceHandlers/Monitoring/Monitoring';
import {
  checkDataFreeTrialEligibility,
  checkFreeTrialEligibility
} from 'services/api/subscriptionApi';
import { TFreeTrialState, TProductType } from 'typings';

export const fetchFreeTrialEligibility = createAsyncThunk(
  'users/fetchFreeTrialEligibility',
  async (
    payload: {
      productType: TProductType;
    },
    thunkAPI
  ) => {
    console.log('calling api');
    try {
      const response = await checkFreeTrialEligibility(payload.productType);
      const responseData = response.data;
      return responseData;
    } catch (error) {
      console.error('Error in fetchFreeTrialEligibility:', error);
      monitoringService.captureException(error, {
        tags: {
          feature: 'fetchFreeTrialEligibility',
          error_type: 'api_error'
        },
        extra: {
          payload
        }
      });
      return thunkAPI.rejectWithValue(
        'Failed to fetch free trial eligibility details'
      );
    }
  }
);

export const fetchDataFreeTrialEligibility = createAsyncThunk(
  'users/fetchDataFreeTrialEligibility',
  async (_: void, thunkAPI) => {
    try {
      const response = await checkDataFreeTrialEligibility();
      return response.data;
    } catch (error) {
      monitoringService.captureException(error, {
        tags: {
          feature: 'fetchDataFreeTrialEligibility',
          error_type: 'api_error'
        }
      });
      return thunkAPI.rejectWithValue(
        'Failed to fetch data free trial eligibility'
      );
    }
  }
);

const initialTrialState: TFreeTrialState = {
  isEligibleForFreeTrialData: false,
  isEligibleForFreeTrialHomeSecurity: false,
  isEligibleForFreeTrialBird: false,
  isEligibleForFreeTrialPet: false,
  isEligibleForFreeTrialTrail: false,
  isEligibleForFreeTrialBaby: false,
  isLoading: false
};

export const freeTrialEligibilitySlice = createSlice({
  name: 'freeTrialEligibility',
  initialState: initialTrialState,
  reducers: {
    updateFreeTrialEligibility: (
      state,
      action: PayloadAction<{
        productType?: TProductType;
        isFreeTrialEligible: boolean;
      }>
    ) => {
      const productType =
        action.payload.productType || PRODUCT_TYPE.HOME_SECURITY; // Default to HomeSecurity if product type is not provided
      switch (productType) {
        case PRODUCT_TYPE.BIRD:
          state.isEligibleForFreeTrialBird = action.payload.isFreeTrialEligible;
          break;
        case PRODUCT_TYPE.PET:
          state.isEligibleForFreeTrialPet = action.payload.isFreeTrialEligible;
          break;
        case PRODUCT_TYPE.TRAIL:
          state.isEligibleForFreeTrialTrail =
            action.payload.isFreeTrialEligible;
          break;
        case PRODUCT_TYPE.BABY:
          state.isEligibleForFreeTrialBaby = action.payload.isFreeTrialEligible;
          break;
        case PRODUCT_TYPE.HOME_SECURITY:
        default:
          state.isEligibleForFreeTrialHomeSecurity =
            action.payload.isFreeTrialEligible;
          break;
      }
    },
    clearFreeTrialEligibility: (state) => {
      state.isEligibleForFreeTrialData = false;
      state.isEligibleForFreeTrialHomeSecurity = false;
      state.isEligibleForFreeTrialBird = false;
      state.isEligibleForFreeTrialPet = false;
      state.isEligibleForFreeTrialTrail = false;
      state.isEligibleForFreeTrialBaby = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDataFreeTrialEligibility.fulfilled, (state, action) => {
        state.isEligibleForFreeTrialData = action.payload.trial;
      })
      .addCase(fetchDataFreeTrialEligibility.rejected, (state) => {
        state.isEligibleForFreeTrialData = false;
      })
      .addCase(fetchFreeTrialEligibility.fulfilled, (state, action) => {
        state.isLoading = false;
        const { productType } = action.meta.arg || {
          productType: PRODUCT_TYPE.HOME_SECURITY
        };
        switch (productType) {
          case PRODUCT_TYPE.BIRD:
            state.isEligibleForFreeTrialBird = action.payload.trial;
            break;
          case PRODUCT_TYPE.PET:
            state.isEligibleForFreeTrialPet = action.payload.trial;
            break;
          case PRODUCT_TYPE.TRAIL:
            state.isEligibleForFreeTrialTrail = action.payload.trial;
            break;
          case PRODUCT_TYPE.BABY:
            state.isEligibleForFreeTrialBaby = action.payload.trial;
            break;
          case PRODUCT_TYPE.HOME_SECURITY:
          default:
            state.isEligibleForFreeTrialHomeSecurity = action.payload.trial;
            break;
        }
      })
      .addCase(fetchFreeTrialEligibility.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchFreeTrialEligibility.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

// Action creators are generated for each case reducer function

export const { updateFreeTrialEligibility, clearFreeTrialEligibility } =
  freeTrialEligibilitySlice.actions;

export default freeTrialEligibilitySlice.reducer;
